import React, { FunctionComponent, useState } from 'react';
import { Button, Dropdown, Modal } from 'rs-emd-ui-atoms';
import { getLabel } from '../../../helpers/html.utils';
import { ILabel } from '../../../models/common/label.model';
import styles from './calibration-component.module.scss';
import { IPDPCalibrationDetails } from '../../../models/pdp/pdp-calibration.model';
import { IPrice } from '../../../models/common/pricing/price.model';
import { IProductService } from '../../../services/product/product-service';

type CalibrationProps = {
  labels?: ILabel[] | null;
  isInFixedCTA?: boolean;
  calibrationDetails: IPDPCalibrationDetails[];
  productService?: IProductService;
  openDropdownCallback?: (isOpen: boolean) => void;
  selectCallback: (stockCode: string) => void;
};

export const Calibration: FunctionComponent<CalibrationProps> = (props: CalibrationProps) => {
  const [calibrationSelectedStockCode, setCalibrationSelectedStockCode] = useState(
    props.calibrationDetails.find((d) => d.isDefault)?.stockCode
  );
  const [isCalibrationModalVisible, setIsCalibrationModalVisible] = useState(false);

  return (
    <div className={`${styles['calibration-container']} ${props.isInFixedCTA ? '' : styles['not-fixed-cta']}`}>
      <div className={styles['calibration-dropdown-container']}>
        <p>{getLabel(props.labels, 'calibration_type')}</p>
        <Dropdown
          mobileHeaderPlaceholder='Calibration'
          defaultSelection={calibrationSelectedStockCode}
          className={`${styles['calibration-dropdown']} ${props.isInFixedCTA ? styles['calibration-fixed-cta'] : ''}`}
          showSeparatorBetweenElements={true}
          onChangeState={(isOpen) => {
            if (isOpen) {
              props.productService?.calibrationTrackerClickEvent(
                'Calibration_dropdown',
                props.calibrationDetails.find((d) => d.isDefault)?.stockCode ?? ''
              );
            }

            props.openDropdownCallback && props.openDropdownCallback(isOpen);
          }}
          selectCallback={(stockCode) => onSelect(stockCode.toString())}
          list={props.calibrationDetails.map((c) => {
            return {
              key: c.stockCode,
              displayString: c.title,
              elt: (
                <div className={styles['calibration-elt']}>
                  <div>
                    <p className={styles['calibration-name']}>{c.title}</p>
                    <p className={styles['calibration-description']}>{c.description}</p>
                  </div>
                  <div className={styles['calibration-prices']}>
                    {c.pricing.showExVat && (
                      <div className={styles['calibration-price']}>
                        {getPriceBlock(c.pricing.priceExVat)}
                        <p className={styles['calibration-description']}>{getLabel(props.labels, 'ex_vat')}</p>
                      </div>
                    )}
                    <div className={styles['calibration-price']}>
                      {getPriceBlock(c.pricing.price)}
                      {c.pricing.showExVat && <p className={styles['calibration-description']}>{getLabel(props.labels, 'inc_vat')}</p>}
                    </div>
                  </div>
                </div>
              ),
            };
          })}
        />
      </div>
      <Button
        className={styles['calibration-btn']}
        text={getLabel(props.labels, 'learn_more_calibration')}
        buttonType='link'
        onClick={() => setIsCalibrationModalVisible(true)}
      />

      {/** Calibration Modal */}
      {isCalibrationModalVisible && (
        <Modal
          isSidebar={true}
          closeCallback={() => setIsCalibrationModalVisible(false)}
          headerElt={<span>{getLabel(props.labels, 'calibration_modal_header')}</span>}
          headerClassName={styles['calibration-modal-header']}
          className={styles['calibration-modal']}
          isMobileFullScreen={true}>
          <p
            className={styles['calibration-modal-content']}
            dangerouslySetInnerHTML={{
              __html: getLabel(props.labels, 'calibration_modal_content'),
            }}
          />
        </Modal>
      )}
    </div>
  );

  function getPriceBlock(price: IPrice) {
    return (
      <div className={styles.price}>
        <p className={styles['unit-price']}>{price?.priceAfter?.displayValue}</p>
        {price.priceAfter?.value !== price.priceBefore?.value && <p className={styles.discount}>{price.priceBefore?.displayValue}</p>}
      </div>
    );
  }
  function onSelect(stockCode: string) {
    const selectedDetailsTitle = props.calibrationDetails.find((d) => d.stockCode === stockCode)?.title;

    if (selectedDetailsTitle) {
      const info = getClickEventInfo(selectedDetailsTitle);
      props.productService?.calibrationTrackerClickEvent(info, stockCode);
    }

    props.selectCallback(stockCode);
    setCalibrationSelectedStockCode(stockCode);
  }

  function getClickEventInfo(selectedTitle: string) {
    if (selectedTitle === 'No Calibration') {
      return 'Calibration_no_calibration';
    } else if (selectedTitle === 'RS Calibration') {
      return 'Calibration_RS_calibration';
    } else {
      return 'Calibration_UKAS_calibration';
    }
  }
};

export default Calibration;
